import { html, type TemplateResult } from "lit";

/**
 * Base path (relative to the resolved script URL — see `getScriptPath()`)
 * where this deployment's shared chart CSS lives. `vite.config.ts`'s
 * `copyPublicToDist` plugin preserves `public/assets/styles/*` verbatim
 * under `dist/assets/styles/*`, next to the JS chunk `getScriptPath()`
 * resolves against — so `"./styles"` here lands on `assets/styles/base.css`,
 * matching `public/assets/styles/base.css`.
 */
export const DEFAULT_CSS_BASE_PATH = "./styles";

let cachedScriptPath = "";

/**
 * Resolves the directory this bundle was loaded from, so CSS (and other
 * assets) can be fetched relative to *this deployment's own* base path
 * rather than a hardcoded one. Prefers `document.currentScript`; falls back
 * to scanning `<script>` tags for one whose `src` contains
 * "lmax-chart-component" — this repo's own entry point, both in dev
 * (`/lmax-chart-component.ts`) and in its Vite build output
 * (`dist/assets/lmax-chart-component.js`, discovered from that same
 * `<script>` tag in each entry HTML page) — for cases where
 * `currentScript` is unavailable (e.g. once the module has finished
 * executing). Memoized at module scope since the answer can't change within
 * a page load.
 */
export function getScriptPath(): string {
  if (cachedScriptPath) return cachedScriptPath;
  if (typeof document === "undefined") return "";

  let scriptSrc = (document.currentScript as HTMLScriptElement | null)?.src;

  if (!scriptSrc) {
    const scripts = Array.from(document.getElementsByTagName("script"));
    const targetScript = scripts.find((script) => script.src?.includes("lmax-chart-component"));
    scriptSrc = targetScript?.src || "";
  }

  if (scriptSrc) {
    cachedScriptPath = scriptSrc.substring(0, scriptSrc.lastIndexOf("/") + 1);
  }

  return cachedScriptPath;
}

/** What `cssIncludes`/`cssIncludesWithParameter` need from the component calling them. */
export interface CssIncludeHost {
  debugMode: boolean;
  /** Per-instance cache of `getScriptPath()`'s result — set by these helpers on first use. */
  cachedScriptPath: string;
  /** Marks the host CSS-ready (e.g. `this.setAttribute("css-ready", "")`) once its stylesheet has settled. */
  _markCssReady(): void;
}

/**
 * Base to resolve CSS URLs against.
 *
 * In dev, `getScriptPath()` resolves to the site *root* (`/lmax-chart-component.ts`
 * is served there directly, not under `/assets/`), whereas in a real build
 * it resolves to the JS chunk's own `assets/` directory (see
 * `DEFAULT_CSS_BASE_PATH`'s doc comment) — so a plain `getScriptPath()` would
 * put dev one path segment short of where this repo's own CSS actually
 * lives (`public/assets/...`, served at `/assets/...`). `import.meta.env.DEV`
 * stands in for that missing `assets/` segment directly, matching the same
 * `import.meta.env.DEV` branch every `defaultStyleHref` getter in this
 * codebase already uses for the same reason.
 *
 * Falls back to `document.baseURI` when neither applies — e.g. an embed
 * context where `getScriptPath()` couldn't find a matching `<script>` tag —
 * so resolution still produces *some* usable URL instead of throwing
 * (`new URL(..., "")` is invalid).
 */
function resolveCssBase(host: CssIncludeHost): string {
  if (import.meta.env.DEV) {
    return `${window.location.origin}/assets/`;
  }
  if (!host.cachedScriptPath) {
    host.cachedScriptPath = getScriptPath();
  }
  return host.cachedScriptPath || document.baseURI;
}

/**
 * Renders the shared `base.css` link alongside a caller-supplied stylesheet
 * (`customPath`, resolved relative to `host`'s own script path — see
 * `getScriptPath()`), both preloaded and linked. Marks `host` CSS-ready once
 * the custom sheet has settled, loaded or errored.
 *
 * Returns `undefined` (renders nothing) when `customPath` is empty — call
 * `cssIncludes(host)` instead in that case.
 */
export function cssIncludesWithParameter(
  host: CssIncludeHost,
  customPath: string,
): TemplateResult | undefined {
  if (host.debugMode) {
    console.log("overrideCSSPath in css includes", customPath);
  }

  const base = resolveCssBase(host);

  if (!customPath) return undefined;

  const cssPath = new URL(DEFAULT_CSS_BASE_PATH, base).href;
  const customCSSPath = new URL(customPath, base).href;

  return html`
    <link rel="preload" href="${cssPath}/base.css" as="style">
    <link rel="preload" href="${customCSSPath}" as="style">
    <link rel="stylesheet" href="${cssPath}/base.css">
    <link rel="stylesheet" href="${customCSSPath}"
          @load=${() => host._markCssReady()}
          @error=${() => host._markCssReady()}>
  `;
}

/** The no-override counterpart to `cssIncludesWithParameter` — just the shared `base.css`. */
export function cssIncludes(host: CssIncludeHost): TemplateResult {
  const cssPath = new URL(DEFAULT_CSS_BASE_PATH, resolveCssBase(host)).href;

  return html`
    <link rel="preload" href="${cssPath}/base.css" as="style">
    <link rel="stylesheet" href="${cssPath}/base.css"
          @load=${() => host._markCssReady()}
          @error=${() => host._markCssReady()}>
  `;
}
